import { useMemo, useState, type KeyboardEvent } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  SearchIcon,
  SearchXIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneCard, PhoneCardList } from '@/components/ui/phone/PhoneCardList';
import { cn } from '@/lib/utils';
import { ChipRow } from '@/shared/components/ChipRow';
import { getExportOrganizationWorkspacesUrl } from '@/lib/api/generated/api';
import type {
  AdminWorkspaceView,
  WorkspaceOwner,
} from '@/lib/api/generated/model';
import { EmptyState } from '@/shared/components/EmptyState';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { RetryButton } from '@/shared/components/RetryButton';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import type { useAdminWorkspaces } from '../hooks/useAdminWorkspaces';
import {
  VISIBILITY_FILTERS,
  VISIBILITY_ICON,
  WORKSPACE_FILTERS,
  WORKSPACES_COPY as WORDS,
  matchesWorkspace,
  nextWorkspaceSort,
  parseWorkspaceSort,
  sortWorkspaces,
  type VisibilityFilter,
  type WorkspaceSortKey,
} from '../workspacesAdmin';
import type {
  WorkspacesView,
  WorkspacesViewChange,
} from './AdminWorkspacesPage';
import { WorkspacesTableSkeleton } from './AdminSkeletons';
import { ExportButton } from './ExportButton';

/** How many owners the stack draws before it says "+N". */
const STACKED = 3;

/**
 * A filter chip: foreground words with a primary outline when pressed, so a
 * pressed chip never sets small text in Light Blue on the dark theme (WD-04).
 */
const CHIP =
  'focus-visible:ring-ring touch-target inline-flex min-h-9 pointer-coarse:min-h-11 items-center gap-1.5 rounded-full border px-3 py-1 text-sm whitespace-nowrap focus-visible:ring-3 focus-visible:outline-none';
const CHIP_PRESSED = 'border-primary/50 bg-primary/10 text-foreground';
const CHIP_IDLE = 'border-border bg-card text-foreground hover:bg-accent/50';

/**
 * Every workspace, as a table to scan (spec 191, spec 195): searchable by name or
 * owner, sliced to those without an active owner or by visibility, sorted by any
 * column the address names — by last activity when it names none. Read whole, so
 * the chips' counts are true for all of them.
 *
 * A row opens the workspace's sheet, by pointer or by Enter and Space; the rows
 * are the page's one interactive list, so they carry the focus ring and
 * `aria-selected`, as the Limits table's do.
 */
export function WorkspacesTable({
  workspaces,
  view,
  onChange,
}: {
  workspaces: ReturnType<typeof useAdminWorkspaces>;
  view: WorkspacesView;
  onChange: WorkspacesViewChange;
}) {
  const showLoading = usePendingDisplay(workspaces.isPending);
  const isPhone = usePhoneWidth();
  const items = useMemo(
    () => workspaces.data?.items ?? [],
    [workspaces.data?.items],
  );
  const rows = useMemo(
    () =>
      sortWorkspaces(
        items.filter((item) =>
          matchesWorkspace(item, view.q, view.show, view.visibility),
        ),
        view.sort,
        view.show,
      ),
    [items, view.q, view.show, view.visibility, view.sort],
  );
  // Each chip counts what it would keep with the other row of chips as it is.
  const counts = {
    all: items.filter((item) =>
      matchesWorkspace(item, '', 'all', view.visibility),
    ).length,
    without_owner: items.filter((item) =>
      matchesWorkspace(item, '', 'without_owner', view.visibility),
    ).length,
  };
  const visibilityCounts = Object.fromEntries(
    VISIBILITY_FILTERS.map((option) => [
      option,
      items.filter((item) => matchesWorkspace(item, '', view.show, option))
        .length,
    ]),
  ) as Record<VisibilityFilter, number>;

  return (
    <section
      aria-labelledby="admin-workspaces-list"
      className="flex min-w-0 flex-col gap-3"
    >
      <h2 id="admin-workspaces-list" className="sr-only">
        <EyebrowLabel>{WORDS.list}</EyebrowLabel>
      </h2>
      <Toolbar
        view={view}
        counts={counts}
        visibilityCounts={visibilityCounts}
        onChange={onChange}
      />

      {workspaces.isPending ? (
        showLoading ? (
          <WorkspacesTableSkeleton />
        ) : null
      ) : workspaces.isError || workspaces.data === undefined ? (
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-sm">{WORDS.failed}</p>
          <RetryButton
            onRetry={workspaces.refetch}
            isFetching={workspaces.isFetching}
          />
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">{WORDS.none}</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={SearchXIcon}
          description={WORDS.noneMatch}
          action={
            <Button
              variant="outline"
              onClick={() =>
                onChange({ q: '', show: 'all', visibility: 'any' })
              }
            >
              {WORDS.clear}
            </Button>
          }
        />
      ) : isPhone ? (
        <PhoneCardList label={WORDS.list}>
          {rows.map((item) => (
            <WorkspaceCard
              key={item.id}
              item={item}
              selected={item.id === view.workspace}
              onOpen={() => onChange({ workspace: item.id }, { push: true })}
            />
          ))}
        </PhoneCardList>
      ) : (
        <div className="border-border bg-card overflow-hidden rounded-md border">
          <div className="max-w-full overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-border text-muted-foreground border-b text-left text-xs">
                  <SortHeader
                    label={WORDS.columns.name}
                    column="name"
                    view={view}
                    onChange={onChange}
                  />
                  <th scope="col" className="px-3 py-2 font-medium">
                    {WORDS.columns.visibility}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {WORDS.columns.owners}
                  </th>
                  <SortHeader
                    label={WORDS.columns.members}
                    column="members"
                    view={view}
                    onChange={onChange}
                  />
                  <SortHeader
                    label={WORDS.columns.activity}
                    column="activity"
                    view={view}
                    onChange={onChange}
                  />
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {rows.map((item) => (
                  <WorkspaceRow
                    key={item.id}
                    item={item}
                    selected={item.id === view.workspace}
                    onOpen={() =>
                      onChange({ workspace: item.id }, { push: true })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function Toolbar({
  view,
  counts,
  visibilityCounts,
  onChange,
}: {
  view: WorkspacesView;
  counts: Record<(typeof WORKSPACE_FILTERS)[number], number>;
  visibilityCounts: Record<VisibilityFilter, number>;
  onChange: WorkspacesViewChange;
}) {
  const [draft, setDraft] = useState(view.q);
  const [lastQuery, setLastQuery] = useState(view.q);
  if (view.q !== lastQuery) {
    setLastQuery(view.q);
    setDraft(view.q);
  }
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="relative min-w-48 flex-1">
        <span className="sr-only">{WORDS.search}</span>
        <SearchIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          type="search"
          value={draft}
          placeholder={WORDS.search}
          onChange={(event) => {
            setDraft(event.target.value);
            setLastQuery(event.target.value);
            onChange({ q: event.target.value });
          }}
          className="ps-8"
        />
      </label>
      <ChipRow label={WORDS.filtersLabel}>
        {WORKSPACE_FILTERS.map((option) => {
          const pressed = option === view.show;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={pressed}
              // The slice decides the order it opens in (FR-002), so a chip
              // drops an order the address held for the other slice.
              onClick={() => onChange({ show: option, sort: undefined })}
              className={cn(CHIP, pressed ? CHIP_PRESSED : CHIP_IDLE)}
            >
              {WORDS.filters[option]}{' '}
              <span className="text-muted-foreground tabular-nums">
                {counts[option]}
              </span>
            </button>
          );
        })}
      </ChipRow>
      <ChipRow label={WORDS.visibilityLabel}>
        {VISIBILITY_FILTERS.map((option) => {
          const pressed = option === view.visibility;
          const Icon = option === 'any' ? null : VISIBILITY_ICON[option];
          return (
            <button
              key={option}
              type="button"
              aria-pressed={pressed}
              onClick={() => onChange({ visibility: option })}
              className={cn(CHIP, pressed ? CHIP_PRESSED : CHIP_IDLE)}
            >
              {Icon ? (
                <Icon
                  aria-hidden="true"
                  strokeWidth={1.5}
                  className="text-muted-foreground size-3.5"
                />
              ) : null}
              {WORDS.visibilityFilters[option]}{' '}
              <span className="text-muted-foreground tabular-nums">
                {visibilityCounts[option]}
              </span>
            </button>
          );
        })}
      </ChipRow>
      <div className="ms-auto max-md:w-full max-md:[&>*]:w-full">
        <ExportButton
          label={WORDS.exportWorkspaces}
          url={getExportOrganizationWorkspacesUrl()}
          filename={`workspaces-${today}.csv`}
        />
      </div>
    </div>
  );
}

function SortHeader({
  label,
  column,
  view,
  onChange,
}: {
  label: string;
  column: WorkspaceSortKey;
  view: WorkspacesView;
  onChange: WorkspacesViewChange;
}) {
  const sort = parseWorkspaceSort(view.sort, view.show);
  const active = sort.key === column;
  return (
    <th
      scope="col"
      aria-sort={
        active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      className="px-3 py-2 font-medium"
    >
      <button
        type="button"
        onClick={() =>
          onChange({ sort: nextWorkspaceSort(view.sort, column, view.show) })
        }
        className="hover:text-foreground focus-visible:ring-ring touch-target inline-flex items-center gap-1 rounded-sm focus-visible:ring-3 focus-visible:outline-none"
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUpIcon aria-hidden="true" className="size-3" />
          ) : (
            <ArrowDownIcon aria-hidden="true" className="size-3" />
          )
        ) : null}
      </button>
    </th>
  );
}

function WorkspaceRow({
  item,
  selected,
  onOpen,
}: {
  item: AdminWorkspaceView;
  selected: boolean;
  onOpen: () => void;
}) {
  const Icon = VISIBILITY_ICON[item.visibility];
  return (
    <tr
      aria-label={item.name}
      data-workspace-id={item.id}
      tabIndex={0}
      aria-selected={selected}
      onClick={onOpen}
      onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'focus-visible:ring-ring cursor-pointer transition-colors focus-visible:ring-3 focus-visible:outline-none',
        selected ? 'bg-selected' : 'hover:bg-accent/50',
      )}
    >
      <td className="px-3 py-2 align-middle">
        <span className="text-foreground font-medium [overflow-wrap:anywhere]">
          {item.name}
        </span>
      </td>
      <td className="text-muted-foreground px-3 py-2 align-middle whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">
          <Icon aria-hidden="true" strokeWidth={1.5} className="size-3.5" />
          {WORDS.visibility[item.visibility]}
        </span>
      </td>
      <td className="px-3 py-2 align-middle">
        <Owners item={item} />
      </td>
      <td className="text-foreground px-3 py-2 align-middle tabular-nums">
        {item.member_count}
      </td>
      <td className="text-muted-foreground px-3 py-2 align-middle whitespace-nowrap">
        <time
          dateTime={item.last_activity_at}
          title={formatExactTime(item.last_activity_at)}
        >
          {formatRelativeTime(item.last_activity_at)}
        </time>
      </td>
    </tr>
  );
}

/**
 * A workspace as a card on a phone (spec 263): its name and visibility, the
 * owners under it, then members and last activity on one line.
 */
function WorkspaceCard({
  item,
  selected,
  onOpen,
}: {
  item: AdminWorkspaceView;
  selected: boolean;
  onOpen: () => void;
}) {
  const Icon = VISIBILITY_ICON[item.visibility];
  return (
    <PhoneCard
      data-workspace-id={item.id}
      selected={selected}
      onOpen={onOpen}
      title={item.name}
      badges={
        <span className="text-muted-foreground inline-flex items-center gap-1.5 text-sm whitespace-nowrap">
          <Icon aria-hidden="true" strokeWidth={1.5} className="size-3.5" />
          {WORDS.visibility[item.visibility]}
        </span>
      }
      lines={[
        <Owners key="owners" item={item} />,
        <span key="facts" className="tabular-nums">
          {WORDS.columns.members} {item.member_count} ·{' '}
          <time
            dateTime={item.last_activity_at}
            title={formatExactTime(item.last_activity_at)}
          >
            {formatRelativeTime(item.last_activity_at)}
          </time>
        </span>,
      ]}
    />
  );
}

/**
 * The owners as an avatar stack with the first one named (US2): a deactivated
 * owner's disc greyed and their name struck through, and "No active owner" when
 * none of them can act.
 */
function Owners({ item }: { item: AdminWorkspaceView }) {
  const [first, ...rest] = item.owners;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {item.owners.length > 0 ? (
        <span className="flex items-center -space-x-1.5">
          {item.owners.slice(0, STACKED).map((owner) => (
            <OwnerDisc key={owner.user.id} owner={owner} />
          ))}
        </span>
      ) : null}
      {first ? (
        <span
          className={
            first.is_active
              ? 'text-foreground'
              : 'text-muted-foreground line-through'
          }
        >
          {first.user.display_name}
          {first.is_active ? null : (
            <span className="sr-only"> ({WORDS.deactivated})</span>
          )}
        </span>
      ) : null}
      {rest.length > 0 ? (
        <span
          className="text-muted-foreground text-xs tabular-nums"
          title={rest
            .map((owner) =>
              owner.is_active
                ? owner.user.display_name
                : WORDS.deactivatedOwner(owner.user.display_name),
            )
            .join(', ')}
        >
          +{rest.length}
        </span>
      ) : null}
      {item.has_active_owner ? null : (
        <Badge
          variant="outline"
          className="border-board-state-waiting/50 text-board-state-waiting w-fit"
        >
          {WORDS.noActiveOwner}
        </Badge>
      )}
    </span>
  );
}

function OwnerDisc({ owner }: { owner: WorkspaceOwner }) {
  return (
    <UserAvatar
      id={owner.user.id}
      displayName={owner.user.display_name}
      email={owner.user.email}
      label={
        owner.is_active
          ? owner.user.display_name
          : WORDS.deactivatedOwner(owner.user.display_name)
      }
      className={cn(
        'ring-card ring-2',
        owner.is_active ? undefined : 'opacity-50 grayscale',
      )}
    />
  );
}
